import React, { useEffect, useRef, useState } from "react";
import { useTheme } from "../../../../context/useThemeContext";
import useDataStore from "../../../../stores/dataStore";
import useAmtOfNotesInFolder from "./hooks/useAmtOfNotesInFolder";

function FolderTitle(props: {
    id: string | undefined;
    isRenamingFolder: boolean;
    setIsRenamingFolder: React.Dispatch<React.SetStateAction<boolean>>;
}) {
    const { theme } = useTheme();

    // Hooks
    const notesAMT = useAmtOfNotesInFolder(props.id);

    return (
        <div className="flex flex-col flex-1 text-nowrap">
            <Title {...props} />

            <p
                className="text-xs"
                style={{
                    color: theme.fontColor[200],
                }}
            >
                {notesAMT} Notes
            </p>
        </div>
    );
}

export default FolderTitle;

function Title(props: {
    id: string | undefined;
    isRenamingFolder: boolean;
    setIsRenamingFolder: React.Dispatch<React.SetStateAction<boolean>>;
}) {
    const { theme } = useTheme();

    // Store
    const folderName = useDataStore((state) =>
        props.id ? state.data.folders[props.id]?.name : "Uncategorized"
    );

    // State for controlled input
    const [inputValue, setInputValue] = useState(folderName);

    // Update input value when folderName changes and not renaming
    useEffect(() => {
        if (!props.isRenamingFolder) {
            setInputValue(folderName);
        }
    }, [folderName, props.isRenamingFolder]);

    // Ref
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (props.isRenamingFolder) inputRef.current?.focus();
        if (props.isRenamingFolder && inputRef.current) {
            inputRef.current.select();
        }
    }, [props.isRenamingFolder]);

    function SetTitle(
        event: React.FocusEvent<HTMLInputElement>,
        title: string
    ) {
        const e = event.currentTarget as HTMLInputElement;
        if (!props.id) return;

        const formattedTitle = title.trim().substring(0, 50);

        props.setIsRenamingFolder(false);
        useDataStore.getState().set.folder(props.id, { name: formattedTitle }); // Remove spaces AND cut to 50 chars
        e.value = formattedTitle;
    }

    return (
        <input
            ref={inputRef}
            className="bg-transparent outline-none text-nowrap whitespace-nowrap text-ellipsis overflow-hidden font-bold rounded-[2px]"
            style={{
                minWidth: 0,
                width: "100%",
                color: theme.fontColor[50],
                lineHeight: "24px",
                minHeight: "24px",
                borderBottom: !props.isRenamingFolder
                    ? "1px solid transparent"
                    : "1px solid " + theme.accentColor,
                pointerEvents: !props.isRenamingFolder ? "none" : "auto",
            }}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onClick={(e) => {
                if (props.isRenamingFolder) {
                    e.preventDefault();
                    e.stopPropagation();
                }
            }}
            onKeyDown={(e) => {
                if (e.key === "Enter") {
                    e.preventDefault();
                    e.stopPropagation();
                    e.currentTarget.blur();
                } else if (e.key === "Escape") {
                    e.preventDefault();
                    e.stopPropagation();
                    // Revert to start
                    setInputValue(folderName);
                    e.currentTarget.blur();
                }
            }}
            onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                window.getSelection()?.removeAllRanges();
                if (!props.id) return;
                props.setIsRenamingFolder(false);
                if (inputValue === folderName) return;
                SetTitle(e, inputValue);
            }}
        />
    );
}
